import { z } from "zod";
import { MEMORY_FIGURES_MIN_USERS } from "./memory.ts";

/**
 * A project's summary (spec `project-home` "Project summary read", design D5): the counts
 * Overview's figure sections show, each over what the caller may read, in one bounded response
 * for the SPA, the CLI and MCP. Counts only: the one text it carries is the title of a top-cited
 * document the caller can read (usage, managers and owners). A part the caller's role may not
 * see is absent, never zero.
 */

const count = z.int().min(0);

export const projectSummaryWikiSchema = z
  .object({
    /** Published pages the caller can read. */
    pages: count,
    /** Of those, pages stale for the caller: a source changed they may see, or a review due. */
    stale: count,
    /** Of those, pages verified once whose current version is newer than the verified one. */
    unverified: count,
    /** Pages stale or unverified, each counted once: the "Need checking" figure. */
    needsChecking: count,
    /** What waits for the caller's review (the review badge's count); absent without drafts. */
    waiting: count.optional(),
    /** The latest publication of a page the caller can read; null when none. */
    lastPublishedAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();

export const projectSummarySourcesSchema = z
  .object({
    /** Files the caller can read. */
    files: count,
    /** Of those, still uploading or processing. */
    processing: count,
    /** Of those, failed: the caller's own uploads, or every one for `file.manage_any`. */
    failed: count,
    /** Files the caller cannot read: only with `file.restricted_count`, never for a guest. */
    restricted: count.optional(),
  })
  .strict();

export const projectSummaryTasksSchema = z
  .object({
    /**
     * The caller's assigned tasks not done, as `GET …/tasks?assignee=me&status=open` lists them
     * (a task whose label they fail is listed without its title, and counted).
     */
    open: count,
    /** Of those, due before today (UTC). */
    overdue: count,
  })
  .strict();

export const projectSummaryMembersSchema = z.object({ members: count, groups: count }).strict();

/** One of the most cited documents the caller can read: its title and citation count. */
export const projectSummaryCitedSchema = z
  .object({
    id: z.uuid(),
    kind: z.enum(["wiki", "file"]),
    title: z.string(),
    citations: count,
  })
  .strict();
export type ProjectSummaryCited = z.infer<typeof projectSummaryCitedSchema>;

export const projectSummaryUsageSchema = z
  .object({
    /** Questions asked in the project over the last 30 days. */
    questions: count,
    /** Distinct people who asked or answered there over the period (at least the floor). */
    activePeople: count,
    /** Answers citing at least one source over all answers, 0–1; null without answers. */
    sourcedShare: z.number().min(0).max(1).nullable(),
    /** At most five, most cited first, among the documents the caller can read. */
    topCited: z.array(projectSummaryCitedSchema).max(5),
    /** When the hourly rollup last ran; null when it never has. */
    rolledUpAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type ProjectSummaryUsage = z.infer<typeof projectSummaryUsageSchema>;

export const projectSummarySchema = z
  .object({
    wiki: projectSummaryWikiSchema,
    sources: projectSummarySourcesSchema,
    tasks: projectSummaryTasksSchema,
    /** For `project.view_members` (readers and up). */
    members: projectSummaryMembersSchema.optional(),
    /**
     * For `project.view_activity` (managers and owners), and only when at least
     * {@link PROJECT_USAGE_MIN_ACTIVE} members were active over the period.
     */
    usage: projectSummaryUsageSchema.optional(),
  })
  .strict();
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

/** Usage is omitted below this many active members (the small-group floor, design D8). */
export const PROJECT_USAGE_MIN_ACTIVE = MEMORY_FIGURES_MIN_USERS;
/** The days Usage covers, today included. */
export const PROJECT_USAGE_DAYS = 30;
/** How many top-cited documents Usage lists. */
export const PROJECT_USAGE_TOP_CITED = 5;
